
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { Building, Factory, Wrench, Briefcase, ArrowRight, Users, Recycle } from "lucide-react";
import { getAllProfessions } from "@/data/professionSolutions";

const Professionnels = () => {
  const services = [
    {
      title: "Industrie",
      description: "Solutions adaptées aux besoins industriels",
      icon: Factory,
      sectors: ["Métallurgie", "Automobile", "Aéronautique", "Chimie"]
    },
    {
      title: "BTP",
      description: "Gestion des déchets de chantier",
      icon: Building,
      sectors: ["Construction", "Rénovation", "Démolition", "Terrassement"]
    },
    {
      title: "Artisans",
      description: "Services pour tous les corps de métiers",
      icon: Wrench,
      sectors: ["Plomberie", "Électricité", "Menuiserie", "Carrelage"]
    },
    {
      title: "Commerce",
      description: "Solutions pour le secteur tertiaire",
      icon: Briefcase,
      sectors: ["Bureaux", "Commerces", "Restaurants", "Hôtels"]
    }
  ];

  const professions = getAllProfessions();
  console.log("All professions loaded:", professions.map(p => p.title));

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Services Professionnels - Groupe Fabre Environnement"
        description="Solutions de gestion des déchets pour professionnels : industrie, BTP, artisans, commerce. Expertise adaptée à chaque secteur."
        canonical="/services/professionnels"
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Services Professionnels</h1>
              <p className="text-xl opacity-90">
                Expertise et solutions sur mesure pour tous les secteurs d'activité. 
                Optimisez votre gestion des déchets.
              </p>
            </div>
          </div>
        </div>

        {/* Services Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="bg-gray-50 p-8 rounded-lg">
                    <div className="w-16 h-16 bg-orange-600 rounded-lg flex items-center justify-center mb-6">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-fabre-gray mb-4">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="space-y-2">
                      {service.sectors.map((sector, idx) => (
                        <span key={idx} className="inline-block bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm mr-2 mb-2">
                          {sector}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Section Solutions par Métier */}
        <section className="py-20 bg-gradient-to-br from-fabre-lightgray to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="flex justify-center mb-6">
                  <div className="bg-fabre-green text-white p-4 rounded-full">
                    <Users className="h-8 w-8" />
                  </div>
                </div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 text-fabre-gray">
                  Solutions par Métier
                </h2>
                <div className="w-24 h-1.5 bg-gradient-to-r from-fabre-green to-fabre-teal mx-auto mb-8 rounded-full"></div>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  Découvrez nos solutions spécialisées adaptées à votre secteur d'activité. 
                  Chaque métier a ses spécificités, nous avons la solution adaptée.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {professions.map((profession, index) => (
                  <Link
                    key={profession.slug}
                    to={`/nos-metiers/${profession.slug}`}
                    className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-fabre-green/30 transform hover:-translate-y-2"
                  >
                    <div className="p-6">
                      <div className="flex items-center mb-4">
                        <div className="bg-gradient-to-br from-fabre-lightgreen to-fabre-green/20 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300">
                          <Recycle className="h-6 w-6 text-fabre-green" />
                        </div>
                        <ArrowRight className="h-5 w-5 text-fabre-green ml-auto opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300" />
                      </div>
                      
                      <h3 className="text-lg font-heading font-bold mb-3 text-fabre-gray group-hover:text-fabre-green transition-colors duration-300">
                        {profession.title.replace("Solutions de recyclage pour ", "")}
                      </h3>
                      
                      <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
                        {profession.description}
                      </p>
                      
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <span className="text-fabre-green font-medium text-sm flex items-center">
                          Voir les solutions
                          <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="text-center">
                <Link
                  to="/nos-metiers"
                  className="inline-flex items-center px-8 py-4 bg-fabre-green hover:bg-fabre-darkgreen text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <Users className="h-5 w-5 mr-3" />
                  Voir tous nos métiers
                  <ArrowRight className="h-5 w-5 ml-3" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Expertise Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-fabre-gray mb-8 text-center">
                Notre expertise à votre service
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-bold text-fabre-gray mb-4">Audit et conseil</h3>
                  <p className="text-gray-600 mb-6">
                    Analyse de vos flux de déchets et optimisation 
                    de votre gestion environnementale.
                  </p>
                  <h3 className="text-2xl font-bold text-fabre-gray mb-4">Solutions personnalisées</h3>
                  <p className="text-gray-600">
                    Mise en place de solutions adaptées à votre activité 
                    et à vos contraintes opérationnelles.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-fabre-gray mb-4">Suivi et reporting</h3>
                  <p className="text-gray-600 mb-6">
                    Traçabilité complète et reporting environnemental 
                    pour vos obligations réglementaires.
                  </p>
                  <h3 className="text-2xl font-bold text-fabre-gray mb-4">Formation</h3>
                  <p className="text-gray-600">
                    Formation de vos équipes aux bonnes pratiques 
                    de tri et de gestion des déchets.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-fabre-gray mb-6">
              Parlons de votre projet
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Chaque entreprise est unique. Contactez-nous pour une étude 
              personnalisée de vos besoins.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded-md font-medium transition-colors"
            >
              Demander une étude
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Professionnels;
